import React, { useEffect, useRef } from 'react';
import { BaseInput, Box, Button, Link, Stack, useToggle } from '@99designs/ui';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';

import {
    getSupportContactUrl,
    getSupportArticleUrl,
} from '@99designs/frontend-components/utils/platform/supportUrlHelpers';
import { FloatingAlert, StatusMessage } from '@99designs/purgatory';
import { ResendTokenPrompt } from './ResendTokenPrompt';
import { ErrorType, UIStateType } from './types';

interface VerifyLoginFormProps {
    inputError: ErrorType | null;
    onSubmit: (token: string) => void;
    requestNewToken: () => void;
    resendRequestCount: number;
    uiState: UIStateType;
}

const errorMessages: { [type in ErrorType]: string } = {
    TOKEN_ATTEMPTS: __('Too many failed attempts. Please <Link>request a new code</Link>'),
    TOKEN_EMPTY: __('Please enter your login code'),
    TOKEN_EXPIRED: __('This code is no longer valid. Please <Link>request a new code</Link>'),
    TOKEN_INVALID: __('Your code is invalid. Please try again'),
};

export function VerifyLoginForm(props: VerifyLoginFormProps) {
    const { inputError, onSubmit, requestNewToken, resendRequestCount, uiState } = props;

    const inputEl = useRef<HTMLInputElement>(null);
    const [showSuccess, { on: toggleOnShowSuccess, off: toggleOffShowSuccess }] = useToggle(false);
    const [
        showDefaultError,
        { on: toggleOnShowDefaultError, off: toggleOffShowDefaultError },
    ] = useToggle(false);

    useEffect(() => {
        if (uiState === 'ERROR' && inputError === null) {
            toggleOnShowDefaultError();
        } else if (uiState === 'SUCCESS' || uiState === 'RESEND_SUCCESS') {
            toggleOnShowSuccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputError, uiState]);

    useEffect(() => {
        if (inputEl.current) {
            inputEl.current.focus();
        }
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const t = inputEl?.current ? inputEl.current.value : '';
        onSubmit(t);
    };

    return (
        <Stack space="halfGutter">
            <form onSubmit={handleSubmit}>
                <Stack space="space10">
                    <CodeInputField
                        ref={inputEl}
                        error={
                            uiState === 'ERROR' && inputError ? (
                                <FormattedReactMessage
                                    message={errorMessages[inputError]}
                                    replacements={{
                                        Link: (
                                            <Link
                                                data-testid="resend-token-error"
                                                color="risingSun"
                                                onClick={requestNewToken}
                                            />
                                        ),
                                    }}
                                />
                            ) : undefined
                        }
                    />
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="baseline"
                        justifyContent="center"
                    >
                        <Button
                            data-testid="verify-token"
                            appearance="primary"
                            width="fill"
                            loading={uiState === 'LOADING'}
                            disabled={uiState === 'LOADING' || uiState === 'RESEND_LOADING'}
                            type="submit"
                        >
                            {__('Log in')}
                        </Button>
                    </Box>
                </Stack>
            </form>
            <ResendTokenPrompt
                requestNewToken={requestNewToken}
                resendRequestCount={resendRequestCount}
            />
            <Box display="flex" flexDirection="row" alignItems="baseline" justifyContent="center">
                <Box whiteSpace="nowrap">
                    <Link href={getSupportArticleUrl('360044399191', __LANG__)}>
                        {__('Why am I seeing this?')}
                    </Link>
                </Box>
            </Box>
            <FloatingAlert
                data-testid="error-message-toast"
                isOpen={showDefaultError}
                message={
                    <FormattedReactMessage
                        message={__('Something went wrong, please <Link>contact support</Link>')}
                        replacements={{
                            Link: <Link color="bleachedAs" href={getSupportContactUrl(__LANG__)} />,
                        }}
                    />
                }
                alertType="error"
                onClose={toggleOffShowDefaultError}
            />
            <FloatingAlert
                data-testid="success-message"
                isOpen={showSuccess}
                message={
                    uiState === 'RESEND_SUCCESS'
                        ? __('Code re-sent')
                        : __('Code accepted. Redirecting... ')
                }
                alertType="success"
                onClose={toggleOffShowSuccess}
            />
        </Stack>
    );
}

const CodeInputField = React.forwardRef(function (
    {
        error,
    }: {
        error?: React.ReactNode;
    },
    ref: React.RefObject<HTMLInputElement>
) {
    return (
        <Stack space="space7">
            {!!error && (
                <StatusMessage type="error">
                    <span data-testid="error-message">{error}</span>
                </StatusMessage>
            )}
            <BaseInput
                error={!!error}
                ref={ref}
                aria-label={__('Enter login code')}
                placeholder={__('Login code')}
                id="verification-token"
                name="token"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
            />
        </Stack>
    );
});
